import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import { formatAmounts } from '@aytm/app/stats/lib/util/helpers'

// styles
import style from '@aytm/app/stats/page/question-views/components/styles/QuestionFooter.module.scss'

// How much the question was answered, drawn once for the question in the slot the template keeps
// below the body. It used to sit inside each block, which is why a block had to work out whether
// its own line would land in the middle of itself -- above a sub-item list it had drawn -- and
// suppress it when it would. The question's slot is always last, so that question no longer
// arises and no block needs to say anything about the count.
//
// Only a question that collected free text has a verbatim count to report, so that half is left
// off the ones that did not: a question with none reads as views alone rather than a zero.
const QuestionFooter = ({ answer, footer, showTotals, hidden }) => {
  // The one question that draws its own line from inside its block -- the storytelling note, whose
  // last-edited text lives there -- leaves the question's own slot empty rather than closing twice.
  if (hidden) return null

  // A question that counts no respondents still closes: it carries the line without a figure, so
  // every question ends on the same edge whether or not it has anything to report there.
  if (!showTotals) return <div className={style.totals}>{footer ?? <>&nbsp;</>}</div>

  // The stat server can still be computing a question after the page has stopped fetching it, so the
  // answer may not have arrived yet. The line then closes the question without a figure, rather than
  // a zero that reads as a count for data not in.
  const counted = null !== answer && 'object' === typeof answer && null != answer.total
  if (!counted) return <div className={style.totals}>&nbsp;</div>

  // The line counts who saw the question -- respondents, whatever base the answers above it are read
  // against -- so switching the report to responses leaves it as it is.
  return (
    <div className={style.totals}>
      <span className={style['totals-responses']}>
        {`${formatAmounts(answer, 'total')} views`}
        {0 < (answer?.comments || 0) ? ` / ${formatAmounts(answer, 'comments')} verbatim` : ''}
      </span>
    </div>
  )
}

QuestionFooter.displayName = 'QuestionFooter'

QuestionFooter.propTypes = {
  answer: answerPropType,
  footer: PropTypes.node,
  hidden: PropTypes.bool,
  // the data-lab tools and the info view answer for something other than a count of respondents
  showTotals: PropTypes.bool
}

export default QuestionFooter
